import { Skeleton } from '@/components/ui/skeleton'
import { TableCell, TableRow } from '@/components/ui/table'
import { StockMove } from '@/interfaces/warehouse/StockMove'
import { cn } from '@/lib/utils'
import { formatMoveDate, formatQuantity, moveTypeLabel, reasonLabel } from '@/lib/warehouse/stockLevels'

interface StockMoveRowProps {
  stockMove?: StockMove
  showSku?: boolean
  showWarehouse?: boolean
}

export const StockMoveRow = ({ stockMove, showSku = true, showWarehouse = false }: StockMoveRowProps) => {
  const span = 5 + Number(showSku) + Number(showWarehouse)

  if (!stockMove) {
    return (
      <TableRow className="hover:bg-transparent">
        {Array.from({ length: span }).map((_, i) => (
          <TableCell key={i} className={cn(i !== (showSku ? 1 : 0) && i !== span - 3 && 'hidden md:table-cell')}>
            <Skeleton className="h-4 w-full max-w-24" />
          </TableCell>
        ))}
      </TableRow>
    )
  }

  const delta = stockMove.quantityDelta
  const reason = reasonLabel(stockMove.reason)
  const mobileDetail = [
    showWarehouse ? stockMove.warehouseName : null,
    moveTypeLabel[stockMove.type] ?? stockMove.type,
    reason,
    stockMove.note,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell
        className={cn('text-muted-foreground', showSku ? 'hidden md:table-cell' : 'w-full max-w-0 md:w-auto md:max-w-none')}
      >
        <span className="block truncate md:inline">{formatMoveDate(stockMove.createdAt)}</span>
        {!showSku && <p className="truncate text-xs md:hidden">{mobileDetail}</p>}
      </TableCell>
      {showSku && (
        <TableCell className="max-w-0 w-full">
          <p className="truncate md:min-w-40">{stockMove.skuName || stockMove.skuCode || stockMove.skuId}</p>
          <p className="truncate text-xs text-muted-foreground md:hidden">
            {[formatMoveDate(stockMove.createdAt), mobileDetail].join(', ')}
          </p>
        </TableCell>
      )}
      {showWarehouse && <TableCell className="hidden md:table-cell">{stockMove.warehouseName}</TableCell>}
      <TableCell className="hidden md:table-cell">{moveTypeLabel[stockMove.type] ?? stockMove.type}</TableCell>
      <TableCell className={cn('text-right font-medium', delta > 0 && 'text-success', delta < 0 && 'text-danger')}>
        {delta > 0 ? '+' : delta < 0 ? '-' : ''}
        {formatQuantity(Math.abs(delta))}
      </TableCell>
      <TableCell className="hidden text-right md:table-cell">{formatQuantity(stockMove.quantityAfter)}</TableCell>
      <TableCell className={cn('hidden max-w-64 md:table-cell', !showSku && 'w-full max-w-0')}>
        <p className="truncate">{reason ?? <span className="text-muted-foreground">-</span>}</p>
        {stockMove.note && <p className="truncate text-xs text-muted-foreground">{stockMove.note}</p>}
      </TableCell>
    </TableRow>
  )
}
